<template>
  <div class="app-data-filter__group">
    <button
      :id="ariaLabelGroupToggler"
      :aria-expanded="opened ? 'true' : 'false'"
      class="app-data-filter__button app-data-filter__group-opener"
      @click="toggleOpenGroup"
    >
      <span class="app-data-filter__group-title"
        >{{ group.title }}<span v-show="checkedValues.length">: {{ checkedValues.length }}</span>
      </span>

      <span v-if="!opened" class="app-data-filter__group-opener-icon">+</span>
      <span v-else class="app-data-filter__group-opener-icon opened">–</span>
    </button>

    <CollapseTransition :duration="200">
      <div v-if="opened" class="app-data-filter__group-items" :aria-labelledby="ariaLabelGroupToggler">
        <div class="app-data-filter__group-all-actions">
          <button
            v-if="group.useSelectAll"
            type="button"
            class="app-data-filter__button"
            :class="{ active: selectedAll }"
            @click="selectAll"
          >
            Select all
          </button>
          <button type="button" class="app-data-filter__button" @click="clearAll">Clear all</button>
        </div>

        <ul v-if="group.items.length" class="app-data-filter__group-items-list">
          <template v-for="item in group.items">
            <li
              v-if="Array.isArray(item.children) && item.children.length"
              :key="`app-filter-${group.itemsType}-${item.id}`"
              class="app-data-filter__group-items-parent-item"
            >
              <p class="app-data-filter__group-items-parent-title">{{ item.title }}</p>

              <ul class="app-data-filter__group-items-parent-list">
                <li
                  v-for="child in item.children"
                  :key="`app-filter-${group.itemsType}-${item.id}-${child.id}`"
                  class="app-data-filter__group-items-parent-list-item"
                >
                  <AppDataFilterCheckboxGroupItem
                    v-model="checkedValues"
                    :item="child"
                    :unique-id="`${group.itemsType}-${item.id}-${child.id}`"
                    @change="onChange"
                  />
                </li>
              </ul>
            </li>

            <li v-else :key="`app-filter-${group.itemsType}-${item.id}`">
              <AppDataFilterCheckboxGroupItem
                v-model="checkedValues"
                :item="item"
                :unique-id="`${group.itemsType}-${item.id}`"
                @change="onChange"
              />
            </li>
          </template>
        </ul>
      </div>
    </CollapseTransition>
  </div>
</template>

<script>
import { isObject } from '@/helpers/common';
import { areArraysEqual, getItemValueAsScalar } from '@/helpers/data-filter/utils';
import AppDataFilterCheckboxGroupItem from './AppDataFilterCheckboxGroupItem';
import CollapseTransition from '@/components/transitions/CollapseTransition';

export default {
  name: 'AppDataFilterCheckboxGroup',
  components: { CollapseTransition, AppDataFilterCheckboxGroupItem },
  props: {
    group: {
      type: Object,
      required: true,
    },
    toggler: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      checkedValues: [],
      opened: false,
    };
  },

  computed: {
    ariaLabelGroupToggler() {
      return `${this.group.itemsType}GroupToggler`;
    },
    selectedAll() {
      return this.checkedValues.length === this.group.items.length;
    },
  },

  watch: {
    toggler: {
      immediate: true,
      handler(newValue) {
        this.opened = newValue;
      },
    },
    value: {
      handler(newVal, oldVal) {
        if (oldVal !== undefined && areArraysEqual(oldVal, newVal)) {
          return;
        }

        const checked = newVal.map((v) => (isObject(v) ? v._filter : v));

        this.checkedValues = this.group.items.flatMap((item) => {
          const valueAsScalar = getItemValueAsScalar(item);
          const selfMatch = checked.includes(valueAsScalar) ? [valueAsScalar] : [];
          const childMatches = (item.children || []).map(getItemValueAsScalar).filter((v) => checked.includes(v));

          return [...selfMatch, ...childMatches];
        });
      },
      immediate: true,
    },
  },

  methods: {
    toggleOpenGroup() {
      this.opened = !this.opened;
      this.$emit('toggle', { itemsType: this.group.itemsType, opened: this.opened });
    },
    onChange() {
      const realValues = this.group.items.flatMap((item) => {
        const valueAsScalar = getItemValueAsScalar(item);
        const selfMatch = this.checkedValues.includes(valueAsScalar) ? [item.value] : [];
        const childMatches = (item.children || [])
          .map((child) => {
            const valueAsScalar = getItemValueAsScalar(child);
            return this.checkedValues.includes(valueAsScalar) ? child.value : null;
          })
          .filter((v) => v);

        return [...selfMatch, ...childMatches];
      });

      this.$emit('update', realValues);
    },
    selectAll() {
      if (this.selectedAll) {
        return;
      }

      this.group.items.forEach((item) => {
        const valueAsScalar = getItemValueAsScalar(item);

        if (!this.checkedValues.includes(valueAsScalar)) {
          this.checkedValues.push(valueAsScalar);
        }
      });
      this.onChange();
    },
    clearAll() {
      if (!this.checkedValues.length) {
        return;
      }

      this.checkedValues = [];
      this.onChange();
    },
  },
};
</script>
